import React, { FC } from 'react';
import BlankLink from '../BlankLink';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '../Tooltip';
import { SpriteIcon } from 'src/config/api/models/FontAwesome';
import { FontAwesomeSpriteIcon } from '../FontAwesomeLocal';
import { useTheme } from '@material-ui/core';
import { styled } from '@material-ui/core';
import { Theme } from '@material-ui/core';

type Props = {
  startIcon?: SpriteIcon;
  isDefault?: boolean;
  isDraft?: boolean;
  isNotVisible?: boolean;
  bookPreview?: boolean;
  previewMode?: boolean;
  previewChangeActions?: boolean;
  actions?: React.ReactNode[];
  link?: string;
  className?: string;
  active?: boolean;
  owner?: string;
  noFlex?: boolean;
};

const Wrapper = styled('div')<Theme>(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  padding: theme.spacing(1, 2),
  width: '100%',
  alignItems: 'center',
  margin: '0 0 0 auto',
  minHeight: '5rem',
  gap: theme.spacing(1),
  borderRadius: theme.config.defaultBorderRadius,
  border: `1px solid transparent`,
  '&.is-default': {
    color: theme.palette.grey['600'],
  },
  '&.with-label': {
    padding: theme.spacing(1, 1, 1, 2),
  },
  '&.active': {
    borderColor: theme.palette.primary.main,
    borderLeftWidth: '5px',
  },
}));

const ActionsWrapper = styled('div')<Theme>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  width: '100%',
  '&.preview-mode': {
    opacity: 0.7,
  },
}));

const StyledBlankLink = styled(BlankLink)<Theme>(({ theme }) => ({
  display: 'flex',
  width: '100%',
  zIndex: 1,
  position: 'relative',
  '&.no-flex': {
    display: 'block',
  },
}));

const StyledLabel = styled('span')<Theme>(({ theme }) => ({
  display: 'flex',
  width: '100%',
  '&.active': {
    color: theme.palette.primary.main,
  },
}));

const Owner = styled('div')<Theme>(({ theme }) => ({
  fontStyle: 'italic',
  color: theme.palette.grey['700'],
}));

const ActiveWrapper = styled('div')({
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  position: 'relative',
});

const LabelWrapper = styled('div')<Theme, { numactions: number }>(({ theme, numactions }) => ({
  width: `calc(100% - ${numactions * 3.5}rem)`,
  '&.preview-mode': {
    opacity: 0.7,
  },
}));

const ListItem: FC<Props> = ({
  startIcon,
  isDefault = false,
  previewMode = false,
  bookPreview = false,
  previewChangeActions = false,
  isDraft = false,
  isNotVisible = false,
  actions,
  link = null,
  className,
  children,
  active = false,
  owner = null,
  noFlex = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Wrapper
      className={`${className} ${isDefault ? 'is-default' : ''} ${active ? 'active' : ''} ${
        isDraft || isNotVisible ? 'with-label' : ''
      }`}
    >
      {startIcon && (
        <FontAwesomeSpriteIcon
          icon={startIcon}
          iconSize="md"
          color={theme.palette.secondary.main}
        />
      )}
      <LabelWrapper
        className={bookPreview && !previewMode ? 'preview' : ''}
        numactions={actions ? actions.length : 0}
      >
        {isDraft || isNotVisible ? (
          <Tooltip title={isDraft ? t('draft.tooltip') : t('notVisible.tooltip')} placement="top">
            <StyledLabel className={active ? 'active' : undefined}>{children}</StyledLabel>
          </Tooltip>
        ) : (
          <>
            {link && (!bookPreview || previewMode) ? (
              <StyledBlankLink to={link} className={noFlex ? 'no-flex' : undefined}>
                {children}
                {owner && <Owner>{owner}</Owner>}
              </StyledBlankLink>
            ) : (
              <ActiveWrapper>
                <StyledLabel className={active ? 'active' : undefined}>{children}</StyledLabel>
                {owner && <Owner>{owner}</Owner>}
              </ActiveWrapper>
            )}
          </>
        )}
      </LabelWrapper>
      {!isDefault && actions && actions.length > 0 && (
        <ActionsWrapper
          className={`hide-on-print ${
            previewChangeActions && bookPreview && !previewMode ? 'preview-mode' : ''
          }}`}
        >
          {actions &&
            actions.map((item: React.ReactNode, index: number) => (
              <React.Fragment key={index}>{item}</React.Fragment>
            ))}
        </ActionsWrapper>
      )}
    </Wrapper>
  );
};

export default ListItem;
